import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CheckBoxButtons from '../../../shared-components/check-box-buttons/CheckBoxButtons';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import deviceApi from '../../../utils/api/device-hub';

// ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only rollout, PR #1130/#1143).
// Re-enable by uncommenting this block once backend security endpoints/fields ship.
/*
import OneTimeKeyModal from './security/OneTimeKeyModal';
import SecurityEditFields from './security/SecurityEditFields';
import {
  DeviceSecurity,
  SecurityEditFormState,
  buildSecurityEditPatch,
  getInitialSecurityEditFormState,
  validateSecurityEditForm,
} from './security/securityProfile';
*/

import '../scss/add-device.scss';

/** Allowed values for the diagnosis/firmware protocol type multi-selects */
const TRANSFER_PROTOCOL_OPTIONS = [
  { label: 'HTTPS', value: 'HTTPS' },
  { label: 'FTPS', value: 'FTPS' },
  { label: 'HTTP', value: 'HTTP' },
  { label: 'FTP', value: 'FTP' },
];

interface AttachedDeviceEditFormProperties {
  /** The device ID */
  deviceId: string;
  brandName: string;
  modelName: string;
  serialNumber: string;
  firmwareProtocolTypes: string[];
  diagnosticProtocolTypes: string[];
  // ⚠ SECURITY PROFILES DISABLED — backend not deployed yet. See imports above.
  // /** The device's current security object, or null if none (legacy pre-profiles charger) */
  // security: DeviceSecurity | null;
  /** Called when the user cancels without saving */
  onCancel: () => void;
  /** Called after a successful save */
  onSaved: () => void;
}

/**
 * Whether two string arrays hold the same values, ignoring order.
 * @param {string[]} a - The first array
 * @param {string[]} b - The second array
 * @returns {boolean} True when they contain the same values
 */
const sameValues = (a: string[], b: string[]): boolean =>
  a.length === b.length && [...a].sort().every((value, index) => value === [...b].sort()[index]);

/**
 * Fetch the device model's own protocol list for a feature, for the "Using the device model's
 * protocols" hint. Best-effort: resolves to `[]` on any error, so the hint falls back to its
 * generic wording rather than failing the form.
 * @param {string} chargingPointSn - The charger's serial number
 * @param {'diagnostics' | 'firmware'} type - Which protocol list to fetch
 * @returns {Promise<string[]>} The model's protocols for that feature
 */
const fetchModelProtocols = async (
  chargingPointSn: string,
  type: 'diagnostics' | 'firmware',
): Promise<string[]> => {
  try {
    const response = await deviceApi.fetchSupportedProtocols(chargingPointSn, type);
    const protocols = response?.data?.protocols || response?.data?.data?.protocols || [];
    return Array.isArray(protocols) ? protocols : [];
  } catch {
    return [];
  }
};

/**
 * Restricted Edit form for a charger that is already attached to an owner: only its firmware
 * and diagnostics protocols and its OCPP security can be changed, via
 * `PATCH /devices/:id/attached-settings` (unlike the generic `PATCH /devices/:id`, which still
 * rejects attached chargers). Clearing a protocol multi-select falls back to the device model's
 * own protocols.
 * @param {object} properties - The component properties
 * @param {string} properties.deviceId - The device ID
 * @param {string} properties.brandName - The charger's brand, shown read-only
 * @param {string} properties.modelName - The charger's model, shown read-only
 * @param {string} properties.serialNumber - The charger's serial number, shown read-only
 * @param {string[]} properties.firmwareProtocolTypes - The charger's current firmware protocols
 * @param {string[]} properties.diagnosticProtocolTypes - The charger's current diagnostic protocols
 * @param {Function} properties.onCancel - Called when the user cancels
 * @param {Function} properties.onSaved - Called after a successful save
 * @returns {React.ReactElement} The form
 */
function AttachedDeviceEditForm({
  deviceId,
  brandName,
  modelName,
  serialNumber,
  firmwareProtocolTypes,
  diagnosticProtocolTypes,
  onCancel,
  onSaved,
}: Readonly<AttachedDeviceEditFormProperties>) {
  const { t } = useTranslation('deviceHub');
  const dispatch = useAppDispatch();

  const [firmware, setFirmware] = useState<string[]>(firmwareProtocolTypes);
  const [diagnostics, setDiagnostics] = useState<string[]>(diagnosticProtocolTypes);
  // ⚠ SECURITY PROFILES DISABLED — backend not deployed yet. See imports above.
  /*
  const [securityEditState, setSecurityEditState] = useState<SecurityEditFormState>(() =>
    getInitialSecurityEditFormState(security),
  );
  */
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /*
  const [oneTimeKey, setOneTimeKey] = useState<string | null>(null);
  */
  // Best-effort protocol names for the "Using the device model's protocols" hint.
  const [modelFirmwareProtocols, setModelFirmwareProtocols] = useState<string[]>([]);
  const [modelDiagnosticProtocols, setModelDiagnosticProtocols] = useState<string[]>([]);

  useEffect(() => {
    if (!serialNumber) return;
    fetchModelProtocols(serialNumber, 'firmware').then(setModelFirmwareProtocols);
    fetchModelProtocols(serialNumber, 'diagnostics').then(setModelDiagnosticProtocols);
  }, [serialNumber]);

  /*
  // Also applies to a charger onboarded before security profiles existed, which has no stored
  // security object yet — the Security section still shows, defaulted to Profile 0.
  const securityErrors = validateSecurityEditForm(security, securityEditState);
  const securityBody = buildSecurityEditPatch(security, securityEditState);
  const hasSecurityChanges = Object.keys(securityBody).some((key) => key !== 'changeReason');
  */
  const firmwareChanged = !sameValues(firmware, firmwareProtocolTypes);
  const diagnosticsChanged = !sameValues(diagnostics, diagnosticProtocolTypes);
  const hasChanges = firmwareChanged || diagnosticsChanged;
  // const transitionPending = security?.transition?.state === 'pending'; — SECURITY PROFILES DISABLED

  /**
   * The "Using the device model's protocols" hint, naming the model's protocols when known.
   * @param {string[]} modelProtocols - The model's protocols for this feature, if fetched
   * @returns {string} The hint text
   */
  const protocolHint = (modelProtocols: string[]): string =>
    modelProtocols.length > 0
      ? t('usingModelProtocolsNamed', { protocols: modelProtocols.join(', ') })
      : t('usingModelProtocols');

  /**
   * Validate, build the diff body, and save.
   * @returns {Promise<void>} Resolves once the request finishes
   */
  const handleSave = async (): Promise<void> => {
    setShowErrors(true);
    if (!hasChanges) return;
    // if (Object.keys(securityErrors).length > 0) return; — SECURITY PROFILES DISABLED

    const body: Record<string, unknown> = {};
    if (firmwareChanged) body.firmwareProtocolTypes = firmware;
    if (diagnosticsChanged) body.diagnosticProtocolTypes = diagnostics;
    // ⚠ SECURITY PROFILES DISABLED — backend not deployed yet. Do not send a `security` key
    // until backend support ships. Re-enable by uncommenting this once it does.
    // if (hasSecurityChanges) body.security = securityBody;

    setSubmitting(true);
    try {
      await deviceApi.updateAttachedDeviceSettings(deviceId, body);
      /*
      setSecurityEditState((previous) => ({ ...previous, enteredKey: '' }));
      const generatedKey = response?.data?.authorizationKey;
      if (generatedKey) {
        setOneTimeKey(generatedKey);
      } else {
        onSaved();
      }
      */
      onSaved();
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message: error?.response?.data?.message || t('somethingWentWrong'),
          code: 0,
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="new-device-page-wrap">
      <div className="new-device-panel">
        <div className="new-device-topbar">
          <h2 className="new-device-title">{t('editDevice')}</h2>
          <div className="new-device-actions">
            <MyButton label={t('cancel')} buttonType="error" onClickFunc={onCancel} />
            <MyButton
              label={submitting ? t('saving') : t('update')}
              buttonType="submit"
              onClickFunc={handleSave}
              disableButton={submitting}
              loading={submitting}
            />
          </div>
        </div>

        <div className="new-device-body">
          <div className="nd-section">
            <div className="nd-section-header">
              <div>
                <p className="nd-section-title">{t('deviceTypeAndModel')}</p>
                <p className="nd-section-subtitle">{t('attachedDeviceEditNote')}</p>
              </div>
            </div>
            <div className="nd-grid-4">
              <div className="nd-field">
                <CustomInput type="text" name="brandName" inputLabel={t('brandLabel')} placeholder="" value={brandName} readOnly fontSize="small" />
              </div>
              <div className="nd-field">
                <CustomInput type="text" name="modelName" inputLabel={t('modelLabel')} placeholder="" value={modelName} readOnly fontSize="small" />
              </div>
              <div className="nd-field">
                <CustomInput
                  type="text"
                  name="serialNumber"
                  inputLabel={t('deviceIdLabel')}
                  placeholder=""
                  value={serialNumber}
                  readOnly
                  fontSize="small"
                />
              </div>
            </div>
          </div>

          <div className="nd-section">
            <div className="nd-section-header">
              <div>
                <p className="nd-section-title">{t('protocols')}</p>
              </div>
            </div>
            <div className="nd-grid-2">
              <div className="nd-field">
                <CheckBoxButtons
                  inputLabel={t('firmwareProtocolTypes')}
                  name="firmwareProtocolTypes"
                  list={TRANSFER_PROTOCOL_OPTIONS}
                  value={firmware}
                  onChangeValue={(_, next) => setFirmware(next as string[])}
                />
                {firmware.length === 0 && (
                  <p className="sp-helper-text">{protocolHint(modelFirmwareProtocols)}</p>
                )}
              </div>
              <div className="nd-field">
                <CheckBoxButtons
                  inputLabel={t('diagnosticProtocolTypes')}
                  name="diagnosticProtocolTypes"
                  list={TRANSFER_PROTOCOL_OPTIONS}
                  value={diagnostics}
                  onChangeValue={(_, next) => setDiagnostics(next as string[])}
                />
                {diagnostics.length === 0 && (
                  <p className="sp-helper-text">{protocolHint(modelDiagnosticProtocols)}</p>
                )}
              </div>
            </div>
          </div>

          {/* ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only rollout,
              PR #1130/#1143). Re-enable by uncommenting this block once backend security
              endpoints/fields ship.
          <div className="nd-section">
            <div className="nd-section-header">
              <div>
                <p className="nd-section-title">{t('security')}</p>
                <p className="nd-section-subtitle">{t('securitySectionDesc')}</p>
              </div>
            </div>
            <SecurityEditFields
              security={security ?? {}}
              value={securityEditState}
              onChange={setSecurityEditState}
              showErrors={showErrors}
              errors={securityErrors}
              sectionDisabled={transitionPending}
              showTransitionPendingNote={transitionPending}
            />
          </div>
          */}

          {showErrors && !hasChanges && <p className="sp-error-text">{t('noSecurityChanges')}</p>}
        </div>
      </div>

      {/* ⚠ SECURITY PROFILES DISABLED — backend not deployed yet. Re-enable alongside the
          Security section once backend security endpoints/fields ship.
      <OneTimeKeyModal
        authorizationKey={oneTimeKey}
        onConfirm={() => {
          setOneTimeKey(null);
          onSaved();
        }}
      /> */}
    </div>
  );
}

export default AttachedDeviceEditForm;
